import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import LabNavigation from '@/components/LabNavigation';
import {
  NotebookBackground,
  DoodleLightbulb,
  DoodleCheckmark,
  DoodleSpiral,
  DoodleStar,
  CoffeeStain,
  PaperClip,
} from '@/components/notebook';

interface Insight {
  number: string;
  title: string;
  hook: string;
  content: React.ReactNode;
  takeaway: string;
  doodle?: 'star' | 'lightbulb' | 'checkmark' | 'spiral';
  stickyColor?: 'yellow' | 'pink' | 'blue' | 'green';
}

const insights: Insight[] = [
  {
    number: "01",
    title: "Context is the New Oil",
    hook: "The first thing AI taught us",
    content: (
      <>
        <p className="mb-4">
          We started with a simple observation: knowledge workers spend <span className="font-semibold underline decoration-wavy decoration-amber-400">2.5 hours daily</span> just searching for information.
          Not creating. Not thinking. Just hunting through Slack threads, Google Docs, and email chains.
        </p>
        <p className="mb-4">
          The breakthrough wasn't building smarter search. It was understanding that <span className="font-semibold">context isn't data—it's relationships</span>.
          Who said what, when, why, and what happened next. That's what makes information useful.
        </p>
        <p>
          We built a "Contextual Intelligence Engine" that didn't just store information—it understood how pieces connected.
          A design decision linked to the meeting where it was made, linked to the person who proposed it, linked to the outcome.
        </p>
      </>
    ),
    takeaway: "AI without context is just expensive autocomplete. Context without AI is just expensive storage.",
    doodle: 'lightbulb',
    stickyColor: 'yellow',
  },
  {
    number: "02",
    title: "The Interface is the Intelligence",
    hook: "Why most AI products feel broken",
    content: (
      <>
        <p className="mb-4">
          Here's what nobody tells you about building AI products: <span className="font-semibold underline decoration-wavy decoration-rose-400">the model is 20% of the work</span>.
          The interface is everything else.
        </p>
        <p className="mb-4">
          Early prototypes were powerful but unusable. We had AI that could summarize a month of Slack in seconds,
          but users didn't trust it. The insight was buried. The magic was invisible.
        </p>
        <p className="mb-4">
          Prateek pushed us to think differently: <span className="italic font-handwritten text-lg">"Don't show people AI. Show them their work, made easier."</span>
        </p>
        <p>
          We redesigned everything. The AI became invisible—working behind the scenes while the interface showed
          exactly what users expected: their tasks, their context, their next move. Trust went up. Adoption followed.
        </p>
      </>
    ),
    takeaway: "The best AI products don't feel like AI products. They feel like superpowers you forgot you had.",
    doodle: 'star',
    stickyColor: 'pink',
  },
  {
    number: "03",
    title: "Workflows Beat Features",
    hook: "The startup graveyard is full of feature-rich products",
    content: (
      <>
        <p className="mb-4">
          We built 47 features in the first three months. Users engaged with maybe 5.
        </p>
        <p className="mb-4">
          The problem wasn't the features—it was the frame. We were thinking in <span className="font-semibold">capabilities</span>
          {" "}(what can AI do?) instead of <span className="font-semibold">workflows</span> (what are people trying to accomplish?).
        </p>
        <p className="mb-4">
          Abhishek, coming from his product experience at Blinkit, saw it clearly:
          <span className="italic font-handwritten text-lg"> "Nobody wakes up wanting to use AI. They wake up wanting to ship a feature, close a deal, or finish a deck."</span>
        </p>
        <p>
          We rebuilt around "Adaptive Workflows"—templates that understood project phases. During ideation, AI was creative and expansive.
          During execution, it was focused and task-oriented. Same AI, different personality based on what you needed.
        </p>
      </>
    ),
    takeaway: "Features solve problems. Workflows solve jobs. Build for jobs.",
    doodle: 'checkmark',
    stickyColor: 'green',
  },
  {
    number: "04",
    title: "Human + AI > Human or AI",
    hook: "The collaboration nobody expected",
    content: (
      <>
        <p className="mb-4">
          The original vision was ambitious: AI that could do 80% of knowledge work automatically.
          After six months of building, we learned something humbling.
        </p>
        <p className="mb-4">
          Pure automation failed on anything creative or ambiguous. But pure human work was slow and inconsistent.
          The magic was in the <span className="font-semibold underline decoration-wavy decoration-teal-400">handoff points</span>.
        </p>
        <p className="mb-4">
          AI drafts, humans refine. AI surfaces options, humans decide. AI tracks patterns, humans interpret meaning.
          We called it "AI/Human Harmony"—not because it sounded good, but because that's literally what worked.
        </p>
        <p>
          The "Expertise Marketplace" emerged from this: a blend of AI tools and human experts you could tap
          when the AI hit its limits. Sometimes you need a model. Sometimes you need a mentor.
        </p>
      </>
    ),
    takeaway: "The future isn't AI replacing humans. It's AI amplifying what humans do best.",
    doodle: 'spiral',
    stickyColor: 'blue',
  },
  {
    number: "05",
    title: "Speed is a Feature",
    hook: "What Prateek taught me about shipping",
    content: (
      <>
        <p className="mb-4">
          Prateek had scaled FairDee to acquisition by Qoala. He'd seen what moves fast and what doesn't.
          His mantra was simple: <span className="font-semibold underline decoration-wavy decoration-orange-400">"Ship something real every week."</span>
        </p>
        <p className="mb-4">
          Not mockups. Not plans. Working code in front of users.
        </p>
        <p className="mb-4">
          This forced brutal prioritization. We couldn't build the perfect AI document generator if we had one week.
          So we built one that was 60% as good but actually existed. Users gave feedback. We iterated. Version 7 was
          better than our original "perfect" version would have been.
        </p>
        <p>
          Applied AI was moving so fast in 2023 that any plan older than a month was already outdated.
          Speed wasn't just about competition—it was about learning before the landscape shifted beneath us.
        </p>
      </>
    ),
    takeaway: "In AI, a shipped imperfection beats an unshipped perfection every time.",
    doodle: 'lightbulb',
    stickyColor: 'yellow',
  },
  {
    number: "06",
    title: "The OS Layer Wins",
    hook: "Where work is actually heading",
    content: (
      <>
        <p className="mb-4">
          Here's what we believe after two years of building in this space:
        </p>
        <p className="mb-4">
          The future of work isn't another AI tool added to your stack. It's not a smarter Notion or a better Slack.
          It's an <span className="font-semibold underline decoration-wavy decoration-indigo-400">operating system layer</span> that sits beneath all your tools
          and makes them work together.
        </p>
        <p className="mb-4">
          Think about what an OS does for your computer: it manages resources, handles communication between programs,
          provides a consistent interface. Work needs the same thing.
        </p>
        <p>
          TGSD was our attempt at that OS. A place where context flows freely, where AI handles the routine so humans
          can focus on the creative, where the chaos of modern work gets channeled into actual output.
          We called it "TheGetShitDone.AI" because, ultimately, that's all anyone wants—to get work done.
        </p>
      </>
    ),
    takeaway: "The next platform winner won't be another app. It'll be the layer that makes all your apps work as one.",
    doodle: 'star',
    stickyColor: 'pink',
  },
];

const DoodleComponent: React.FC<{ type?: string; color?: string }> = ({ type, color = '#6b7280' }) => {
  switch (type) {
    case 'star': return <DoodleStar size={32} color={color} animated />;
    case 'lightbulb': return <DoodleLightbulb size={32} color={color} animated />;
    case 'checkmark': return <DoodleCheckmark size={32} color={color} animated />;
    case 'spiral': return <DoodleSpiral size={32} color={color} animated />;
    default: return <DoodleStar size={32} color={color} animated />;
  }
};

const LearningAIStudy = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const { scrollYProgress } = useScroll();
  const progressWidth = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 300);
  }, []);

  return (
    <div className="min-h-screen bg-[#fdfcf7] relative overflow-hidden">
      {/* Graph paper pattern */}
      <div
        className="fixed inset-0 opacity-[0.35] pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(#e8e0d5 1px, transparent 1px),
            linear-gradient(90deg, #e8e0d5 1px, transparent 1px)
          `,
          backgroundSize: '24px 24px',
        }}
      />

      {/* Paper texture */}
      <div
        className="fixed inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Progress Bar - pencil style */}
      <motion.div
        className="fixed top-0 left-0 h-1 bg-gradient-to-r from-gray-600 to-gray-400 z-50"
        style={{ width: progressWidth }}
      />

      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/labs')}
        className="fixed top-8 left-8 z-40 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-typewriter"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium">Back to Labs</span>
      </motion.button>

      {/* Hero - Clean Notebook Style */}
      <section className="min-h-screen flex flex-col justify-center items-center px-8 py-20 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl relative z-10"
        >
          {/* Date label */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="font-typewriter text-sm text-gray-500 mb-4"
          >
            2023 • When Applied AI Was New
          </motion.p>

          {/* Title */}
          <h1 className="text-5xl md:text-7xl font-sketch text-gray-800 mb-4 leading-tight">
            Learning AI
            <br />
            <span className="text-gray-500">by Building AI</span>
          </h1>

          {/* Subtitle */}
          <p className="font-serif text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Three builders tried to create an operating system for work.
            Along the way, we learned more about AI than any course could teach.
          </p>

          {/* The Team - Clean inline */}
          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 mb-10 font-handwritten text-gray-700">
            <a href="/" className="hover:text-gray-900 transition-colors">
              <span className="font-semibold">Harsh Joshi</span>
              <span className="text-gray-400 text-sm ml-1">Engineering</span>
            </a>
            <span className="text-gray-300">•</span>
            <a href="https://linkedin.com/in/prateekjogani" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900 transition-colors">
              <span className="font-semibold">Prateek Jogani</span>
              <span className="text-gray-400 text-sm ml-1">CTO @ Qoala</span>
            </a>
            <span className="text-gray-300">•</span>
            <a href="https://linkedin.com/in/abhishek-bagaria" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900 transition-colors">
              <span className="font-semibold">Abhishek Bagaria</span>
              <span className="text-gray-400 text-sm ml-1">Ex-Blinkit</span>
            </a>
          </div>

          {/* Context quote */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5 }}
            className="max-w-xl mx-auto"
          >
            <p className="text-gray-600 font-serif italic border-l-2 border-gray-300 pl-4 text-left">
              "ChatGPT had just exploded. Everyone was asking 'what can AI do?'
              We asked: <span className="font-semibold not-italic text-gray-800">What should work feel like?</span>"
            </p>
          </motion.div>
        </motion.div>

        {/* Scroll hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-12 text-gray-400 text-xs font-typewriter"
        >
          scroll to read
        </motion.div>
      </section>

      {/* The Problem Section */}
      <section className="py-16 px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <h2 className="font-sketch text-2xl text-gray-800 mb-2">The Problem We Saw</h2>
            <div className="w-16 h-0.5 bg-gray-300"></div>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                title: "Scattered Context",
                desc: "Designer sketches in Figma. PM writes specs in Notion. Leadership's vision buried in Slack. You're the human router connecting it all.",
              },
              {
                title: "Tool Fatigue",
                desc: "The average knowledge worker uses 9.4 apps daily. Each promises productivity. Together, they create chaos.",
              },
              {
                title: "Lost Time",
                desc: "30% of work time spent on 'work about work'—status updates, finding files, re-explaining context. Not actual work.",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4"
              >
                <span className="font-sketch text-gray-300 text-lg">{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">{item.title}</h3>
                  <p className="font-serif text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-10 font-handwritten text-lg text-gray-700 text-center"
          >
            We thought: <span className="font-semibold">What if there was one place where all this just... worked?</span>
          </motion.p>
        </div>
      </section>

      {/* Insights Section */}
      <section className="py-16 px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-sketch text-gray-800 mb-2">What We Learned</h2>
            <p className="font-handwritten text-gray-600">Six insights from building an AI-powered work OS</p>
          </motion.div>

          {insights.map((insight, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              className="mb-16 last:mb-0"
            >
              <NotebookBackground
                variant={index % 2 === 0 ? 'lined' : 'grid'}
                marginLine={index % 2 === 0}
                holes={false}
                aging="light"
                className="rounded-lg shadow-md relative"
              >
                {/* Paper clip on some cards */}
                {index % 3 === 0 && (
                  <PaperClip color={index % 2 === 0 ? 'gold' : 'silver'} position="top-right" rotation={10} />
                )}

                {/* Coffee stain on one card */}
                {index === 2 && (
                  <CoffeeStain size="small" intensity="light" position={{ bottom: '5%', right: '3%' }} />
                )}

                <div className="p-6 md:p-8">
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-6">
                    {/* Number - hand drawn circle */}
                    <div className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-dashed border-gray-400 flex items-center justify-center bg-white/50">
                      <span className="font-sketch text-xl font-bold text-gray-700">{insight.number}</span>
                    </div>

                    <div className="flex-1">
                      <span className="font-handwritten text-sm text-gray-500 italic">{insight.hook}</span>
                      <h3 className="text-2xl md:text-3xl font-sketch text-gray-800">{insight.title}</h3>
                    </div>

                    {/* Doodle */}
                    <div className="flex-shrink-0 hidden md:block">
                      <DoodleComponent type={insight.doodle} color="#6b7280" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="text-gray-700 leading-relaxed font-serif text-base md:text-lg mb-6 pl-0 md:pl-16">
                    {insight.content}
                  </div>

                  {/* Takeaway - as margin note style */}
                  <div className="pl-0 md:pl-16">
                    <div className="flex items-start gap-3 bg-amber-50/80 border-l-4 border-amber-400 p-4 rounded-r-lg">
                      <DoodleLightbulb size={20} color="#f59e0b" />
                      <p className="font-handwritten text-base text-gray-800 italic">
                        {insight.takeaway}
                      </p>
                    </div>
                  </div>
                </div>
              </NotebookBackground>
            </motion.div>
          ))}
        </div>
      </section>

      {/* What We Built Section */}
      <section className="py-16 px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <h2 className="font-sketch text-2xl text-gray-800 mb-2">What We Built</h2>
            <div className="w-16 h-0.5 bg-gray-300 mb-4"></div>
            <p className="font-serif text-gray-600">
              TGSD wasn't just a product—it was a thesis about how work should feel.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
            {[
              { title: "Contextual Intelligence", desc: "All your context—docs, chats, recordings—in one AI-processed knowledge base." },
              { title: "Collaboration Canvas", desc: "Live workspace where your team brainstorms with AI that learns." },
              { title: "Adaptive Workflows", desc: "Playbooks that morph based on project phase and team role." },
              { title: "Expertise Marketplace", desc: "On-demand AI tools and human experts. Model or mentor." },
              { title: "Predictive Analytics", desc: "See bottlenecks forming before they block you." },
              { title: "Scenario Playback", desc: "Time-travel through your project. Replay decisions." },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="border-l-2 border-gray-200 pl-4 py-1"
              >
                <h4 className="font-semibold text-gray-800 text-sm">{item.title}</h4>
                <p className="font-serif text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Reflection */}
      <section className="py-20 px-8">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <blockquote className="text-xl md:text-2xl font-sketch text-gray-800 mb-8 leading-relaxed border-l-4 border-gray-300 pl-6">
              "We didn't just learn how to build AI.
              We learned how work is about to change."
            </blockquote>

            <div className="font-serif text-gray-700 leading-relaxed space-y-4">
              <p>
                The tools we use today—Slack, Notion, Google Docs—were designed for a world before AI.
                They assume humans are the only processors of information.
              </p>
              <p>
                The next generation of work tools will be different. They'll understand context natively.
                They'll adapt to how you work, not force you to adapt to them.
              </p>
              <p className="font-handwritten text-lg text-gray-800 pt-2">
                TGSD was our attempt at that future. We learned more building it than using it.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* For Builders Section */}
      <section className="py-16 px-8 bg-gray-50/50">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="font-sketch text-2xl text-gray-800 mb-2">For Builders Reading This</h2>
            <div className="w-16 h-0.5 bg-gray-300"></div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Start with workflows, not features. Ask 'what job is this solving?' before 'what can AI do?'",
              "Ship weekly. The landscape moves too fast for quarterly roadmaps.",
              "Make AI invisible. The best AI products don't feel like AI products.",
              "Context is everything. Raw data without relationships is noise.",
              "Embrace human-AI collaboration. Pure automation fails on ambiguity.",
              "Build the OS layer. The future isn't another app—it's the glue between apps.",
            ].map((advice, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-start gap-3"
              >
                <span className="font-typewriter text-sm text-gray-400 mt-0.5">{String(index + 1).padStart(2, '0')}</span>
                <p className="font-serif text-sm text-gray-700 leading-relaxed">{advice}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-8">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-serif text-gray-600 mb-6">
              Watch a walkthrough of what we built
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://www.loom.com/share/7fe82dfee6404292add0e6a33853b187"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-800 text-white text-sm font-medium hover:bg-gray-700 transition-colors"
              >
                Watch Demo
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <a
                href="https://linkedin.com/in/prateekjogani"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-gray-700 text-sm font-medium hover:text-gray-900 transition-colors border border-gray-300"
              >
                Connect with Prateek
              </a>
            </div>

            <p className="mt-10 text-gray-400 text-xs font-typewriter">
              Built by{" "}
              <a href="/" className="text-gray-600 hover:text-gray-800">Harsh</a>,{" "}
              <a href="https://linkedin.com/in/prateekjogani" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-800">Prateek</a>, and{" "}
              <a href="https://linkedin.com/in/abhishek-bagaria" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-800">Abhishek</a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lab Navigation */}
      <LabNavigation currentLabId="learning-ai" variant="light" />
    </div>
  );
};

export default LearningAIStudy;
